import { TRootState } from 'store';
import { createSelector } from '@reduxjs/toolkit';
import moment from 'moment';
import { UserSubscriptionCurrency } from 'features/User/types';

export const getUser = (state: TRootState) => state.user;

export const selectUser = getUser; // Alias for consistency

export const getId = createSelector(getUser, ({ id }) => id);

export const getUsername = createSelector(getUser, ({ username }) => username);

export const getFirstName = createSelector(getUser, ({ firstName }) => firstName);

export const getEmail = createSelector(getUser, ({ email }) => email);

export const getCancellationStep = createSelector(
  getUser,
  ({ cancellationStep }) => cancellationStep
);

export const getSubscription = createSelector(getUser, ({ subscription }) => subscription);

export const getTier = createSelector(getSubscription, subscription => subscription?.tier);
export const getStatus = createSelector(getSubscription, subscription => subscription?.status);

export const getNextPaymentDate = createSelector(getSubscription, subscription => {
  if (!subscription) return '';

  const { nextPaymentDate, paymentFrequencyDays } = subscription;
  
  const formattedDate = moment(nextPaymentDate).format('MMM DD, YYYY');
  
  // For daily subscriptions, also show how many days from now
  if (paymentFrequencyDays === 1) {
    const today = moment().startOf('day');
    const paymentDay = moment(nextPaymentDate).startOf('day');
    const daysFromNow = paymentDay.diff(today, 'days');
    if (daysFromNow === 0) return `${formattedDate} (today)`;
    if (daysFromNow === 1) return `${formattedDate} (tomorrow)`;
  }

  return formattedDate;
});

export const getPaymentFrequencyDays = createSelector(
  getSubscription,
  subscription => subscription?.paymentFrequencyDays ?? null
);

export const getPaymentFrequencyMonths = createSelector(
  getSubscription,
  subscription => {
    if (subscription?.paymentFrequencyDays !== null) {
      return 0;
    }
    return subscription?.paymentFrequencyMonths ?? 6;
  }
);

export const isShortTermSubscription = createSelector(
  getPaymentFrequencyDays,
  paymentFrequencyDays => paymentFrequencyDays !== null
);

export const getPaymentFrequencyText = createSelector(
  getSubscription,
  subscription => {
    if (!subscription) return '';
    
    const { paymentFrequencyDays, paymentFrequencyMonths } = subscription;
    
    if (paymentFrequencyDays !== null) {
      if (paymentFrequencyDays === 1) return 'Daily';
      if (paymentFrequencyDays === 7) return 'Weekly';
      if (paymentFrequencyDays === 14) return 'Bi-weekly';
      if (paymentFrequencyDays % 7 === 0) {
        const weeks = paymentFrequencyDays / 7;
        return `Every ${weeks} weeks`;
      }
      return `Every ${paymentFrequencyDays} days`;
    }
    
    if (paymentFrequencyMonths === 1) return 'Monthly';
    if (paymentFrequencyMonths === 3) return 'Quarterly';
    if (paymentFrequencyMonths === 6) return 'Semi-annually';
    if (paymentFrequencyMonths === 12) return 'Annually';
    return `Every ${paymentFrequencyMonths} months`;
  }
);

export const getPaymentAmount = createSelector(getSubscription, subscription => {
  if (!subscription) return null;

  const { paymentAmount, currency } = subscription;

  const currencySign = (
    {
      USD: '$',
      Euro: '€',
    } as Record<UserSubscriptionCurrency, string>
  )[currency ?? 'USD'];

  const normalizedPaymentAmount = Number(paymentAmount || 0)
    .toFixed(2)
    .split('.')
    .join(',');

  return `${currencySign}${normalizedPaymentAmount}`;
});

export const getPreferredLanguageCode = createSelector(getUser, ({ preferredLanguageCode }) => preferredLanguageCode);

export const hasPreferredLanguage = createSelector(
  getPreferredLanguageCode,
  preferredLanguageCode => preferredLanguageCode !== null && preferredLanguageCode !== ''
);

export const getUserOnboardedAt = createSelector(getUser, ({ onboardedAt }) => onboardedAt);

export const isUserOnboarded = createSelector(
  getUserOnboardedAt,
  onboardedAt => onboardedAt !== null
);

export const getStudyInNativeLanguage = createSelector(getUser, ({ studyInNativeLanguage }) => studyInNativeLanguage);
